<script setup>
import { ref } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import { useLanding } from "../../composables/useLanding";
import { useStaggerReveal } from "../../composables/useStaggerReveal";
import { useGsap } from "../../composables/useGsap";

const content = useLanding().testimonials;

const list = ref(null);
const { revealed } = useStaggerReveal(list);

// Each quote's edge draws down from the top when the quotes scroll in.
useGsap(list, (gsap) => {
  gsap.from(".quote__edge", {
    scaleY: 0,
    duration: 1.1,
    ease: "expo.out",
    stagger: 0.18,
    scrollTrigger: { trigger: list.value, start: "top 80%", once: true },
  });
});
</script>

<template>
  <section class="section section--canvas" id="testimonials">
    <div class="container">
      <SectionHeading
        eyebrow="מה אומרים עלינו"
        :title="content.title"
      />

      <div ref="list" class="quotes">
        <figure
          v-for="(item, i) in content.items"
          :key="item.name"
          class="quote reveal"
          :class="{ 'is-revealed': revealed }"
          :style="{ '--reveal-index': i }"
        >
          <span class="quote__edge" aria-hidden="true" />
          <blockquote class="quote__text">{{ item.quote }}</blockquote>

          <figcaption class="quote__author">
            <img
              :src="item.avatar"
              alt=""
              class="quote__avatar"
              width="48"
              height="48"
              loading="lazy"
            />
            <span>
              <strong class="quote__name">{{ item.name }}</strong>
              <span class="quote__role">{{ item.role }}</span>
            </span>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
</template>

<style scoped>
.quotes {
  display: grid;
  gap: var(--space-5);
}

/*
 * Open quotes, no card: the words are the content. The thick edge on the
 * reading side is the one the hero's booked slot carries.
 */
.quote {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  margin: 0;
  position: relative;
  padding-inline-start: calc(var(--space-6) + 6px);
}

/* The edge, as an element so it can draw in. */
.quote__edge {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  inline-size: 6px;
  border-radius: var(--radius-pill);
  background: linear-gradient(to bottom, var(--brand), var(--accent));
  transform-origin: top;
}

.quote__text {
  margin: 0;
  color: var(--ink);
  font-size: var(--text-2xl);
  font-weight: var(--weight-medium);
  line-height: var(--leading-normal);
  flex-grow: 1;
}

.quote__author {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.quote__avatar {
  inline-size: 52px;
  block-size: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: var(--surface);
  box-shadow: 0 0 0 3px var(--surface), 0 0 0 5px var(--line);
}

.quote__name {
  display: block;
  color: var(--ink);
}

.quote__role {
  font-size: var(--text-sm);
  color: var(--ink-subtle);
}

/* Scroll-snap carousel where three columns will not fit. */
@media (max-width: 899px) {
  .quotes {
    grid-auto-flow: column;
    grid-auto-columns: min(82%, 340px);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-block-end: var(--space-3);
    margin-inline: calc(var(--gutter) * -1);
    padding-inline: var(--gutter);
    scrollbar-width: thin;
  }

  .quote {
    scroll-snap-align: center;
  }
}

@media (min-width: 900px) {
  /* Sizes to however many quotes there are, centred — two reads as deliberate,
     three fills the row. */
  .quotes {
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 480px);
    gap: var(--space-9);
    justify-content: center;
  }
}
</style>
